import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Modal } from '@mui/material';
import {
  listConnections, createConnection, updateConnection, deleteConnection,
  uploadCertificate, generateCertificate, downloadCertificate,
  testConnection, startDelegatedLogin, getDelegatedStatus, logoutDelegated,
  delegatedRedirectUri,
} from '../../api/sharepointAPI';
import { useNotification } from '../../context/NotificationContext';
import { NotificationType } from '../../enums/NotificationType';
import ConfirmDeleteModal from '../shared/ConfirmDeleteModal';
import CertHelpModal from './CertHelpModal';
import DelegatedHelpModal from './DelegatedHelpModal';
import { formatDate } from '../../utils/dateFormat';

const emptyForm = {
  Name: '', SiteUrl: '', Tenant: '', ClientId: '', AuthType: 'certificate',
  CertPassword: '',
  Enabled: true,
  AutoCert: true,
  HasCertificate: false, CertificateFileName: '',
  HasGeneratedCer: false, CertThumbprint: '', CertNotAfter: '',
  CertReused: false, CertReusedFrom: '',
  HasCertPassword: false, DelegatedAccount: '',
};

const ConnectionsList = ({ onChange }) => {
  const [t, i18n] = useTranslation('global');
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [connections, setConnections] = useState([]);
  const [testing, setTesting] = useState({});
  const [testResult, setTestResult] = useState({});
  const [openModal, setOpenModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [certFile, setCertFile] = useState(null);
  const [genLoading, setGenLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalTest, setModalTest] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [deviceFlow, setDeviceFlow] = useState(null);
  const [showCertHelp, setShowCertHelp] = useState(false);
  const [showDelegatedHelp, setShowDelegatedHelp] = useState(false);
  const [copiedRedirect, setCopiedRedirect] = useState(false);
  const popupRef = useRef(null);

  const fetchConnections = () => {
    listConnections()
      .then(d => { const list = d || []; setConnections(list); if (onChange) onChange(list); })
      .catch(() => {});
  };
  useEffect(() => { fetchConnections(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const finishDelegated = (account) => {
    setForm(current => ({ ...current, DelegatedAccount: account || '', DelegatedAuthenticatedAt: new Date().toISOString() }));
    fetchConnections();
    if (popupRef.current && !popupRef.current.closed) { try { popupRef.current.close(); } catch (e) { /* noop */ } }
    popupRef.current = null;
    setDeviceFlow(null); // žiadne potvrdzovacie okno — účet je hneď vo formulári
    showNotification({
      message: account ? `${t('loginSuccess') || 'Prihlásenie úspešné'} – ${account}` : (t('loginSuccess') || 'Prihlásenie úspešné'),
      type: NotificationType.Success,
    });
  };

  // Priebeh popup prihlásenia — polling stavu (+ detekcia zavretia okna).
  useEffect(() => {
    if (!deviceFlow || deviceFlow.status !== 'pending' || editId === null) return undefined;
    const timer = window.setInterval(async () => {
      try {
        const status = await getDelegatedStatus(editId);
        if (status.status === 'authenticated') {
          finishDelegated(status.account);
        } else if (status.status === 'error') {
          setDeviceFlow(current => current ? { ...current, status: 'error', detail: status.detail } : current);
        } else if (popupRef.current && popupRef.current.closed) {
          setDeviceFlow(null); // okno zavreté bez dokončenia → ticho, tlačidlo sa povolí
        }
      } catch (e) {
        setDeviceFlow(current => current
          ? { ...current, status: 'error', detail: e?.response?.data?.detail || String(e) }
          : current);
      }
    }, 2000);
    return () => window.clearInterval(timer);
  }, [deviceFlow?.status, editId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Okamžité dokončenie: callback stránka pošle postMessage z popupu.
  useEffect(() => {
    const onMessage = (ev) => {
      if (ev.data !== 'sp-delegated-done' || editId === null) return;
      getDelegatedStatus(editId)
        .then(status => { if (status.status === 'authenticated') finishDelegated(status.account); })
        .catch(() => {});
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [editId]); // eslint-disable-line react-hooks/exhaustive-deps

  const openAdd = () => {
    setEditId(null);
    setForm(emptyForm);
    setCertFile(null);
    setModalTest(null);
    setOpenModal(true);
  };
  const openEdit = (c) => {
    setEditId(c.Id);
    setCertFile(null);
    setModalTest(null);
    const hasCert = !!(c.HasCertificate || c.CertPath);
    setForm({
      Name: c.Name || '', SiteUrl: c.SiteUrl || '', Tenant: c.Tenant || '', ClientId: c.ClientId || '',
      AuthType: c.AuthType === 'delegated' ? 'delegated' : 'certificate',
      CertPassword: '',
      Enabled: c.Enabled !== false,
      // Auto režim ak appka cert vygenerovala, alebo ak zatiaľ žiadny cert nie je (default).
      AutoCert: c.HasGeneratedCer ? true : !hasCert,
      HasCertificate: hasCert,
      CertificateFileName: c.CertificateFileName || (c.CertPath ? c.CertPath.split(/[\\/]/).pop() : ''),
      HasGeneratedCer: !!c.HasGeneratedCer, CertThumbprint: c.CertThumbprint || '', CertNotAfter: c.CertNotAfter || '',
      HasCertPassword: !!c.HasCertPassword, DelegatedAccount: c.DelegatedAccount || '',
      DelegatedAuthenticatedAt: c.DelegatedAuthenticatedAt || null,
    });
    setOpenModal(true);
  };
  const closeModal = () => {
    setOpenModal(false);
    setForm(emptyForm);
    setCertFile(null);
    setEditId(null);
    setModalTest(null);
    setDeviceFlow(null);
  };

  const change = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const buildPayload = () => {
    const p = {
      Name: form.Name, SiteUrl: form.SiteUrl, Tenant: form.Tenant, ClientId: form.ClientId,
      AuthType: form.AuthType,
      Enabled: !!form.Enabled,
    };
    if (form.CertPassword) p.CertPassword = form.CertPassword;
    return p;
  };

  const handleSave = async () => {
    if (!form.SiteUrl || !form.Tenant || !form.ClientId) {
      showNotification({ message: t('fillRequired') || 'Vyplň Site URL, Tenant a Client ID', type: NotificationType.Error });
      return;
    }
    // Manuálny režim vyžaduje .pfx; auto-režim si cert vygeneruje sám.
    if (form.AuthType === 'certificate' && !form.AutoCert && !form.HasCertificate && !certFile) {
      showNotification({ message: t('selectCertificate') || 'Vyber certifikát (.pfx)', type: NotificationType.Error });
      return;
    }
    setSaving(true);
    let connectionId = editId;
    try {
      const saved = editId === null
        ? await createConnection(buildPayload())
        : await updateConnection(editId, buildPayload());
      connectionId = editId === null ? saved.Id : editId;
      if (form.AuthType === 'certificate' && !form.AutoCert && certFile) {
        await uploadCertificate(connectionId, certFile);
      }
      // Auto-režim bez certifikátu → appka ho vygeneruje a modal ostane otvorený na stiahnutie .cer.
      // Delegované je tiež confidential klient s certom → generujeme aj preň (bez AutoCert prepínača).
      if (((form.AuthType === 'certificate' && form.AutoCert) || form.AuthType === 'delegated') && !form.HasGeneratedCer) {
        const gen = await generateCertificate(connectionId);
        setEditId(connectionId);
        setForm(f => ({
          ...f, HasCertificate: true, HasGeneratedCer: true,
          CertThumbprint: gen.Thumbprint, CertNotAfter: gen.NotAfter,
          CertReused: !!gen.Reused, CertReusedFrom: gen.FromName || '',
        }));
        fetchConnections();
        showNotification({
          message: gen.Reused
            ? (t('certReusedMsg') || 'Certifikát prevzatý z existujúceho pripojenia — v Azure netreba nič nahrávať.')
            : (t('certGenerated') || 'Certifikát vygenerovaný — stiahnite .cer a nahrajte do Azure.'),
          type: NotificationType.Success,
        });
        setSaving(false);
        return;
      }
      showNotification({ message: t('savedChanges'), type: NotificationType.Success });
      fetchConnections();
      closeModal();
    } catch (e) {
      // Ak vytvorenie prešlo, ale upload zlyhal, ďalší pokus už upraví tú istú konekciu.
      if (editId === null && connectionId !== null) {
        setEditId(connectionId);
        fetchConnections();
      }
      showNotification({
        message: e?.response?.data?.detail || t('errorOccured'),
        type: NotificationType.Error,
      });
    } finally { setSaving(false); }
  };

  const handleCertSelect = (e) => {
    const file = e.target.files && e.target.files[0];
    setCertFile(file || null);
  };

  const handleGenerateCert = async (forceNew = false) => {
    if (editId === null) return;
    setGenLoading(true);
    try {
      const gen = await generateCertificate(editId, forceNew);
      setForm(f => ({
        ...f, HasCertificate: true, HasGeneratedCer: true,
        CertThumbprint: gen.Thumbprint, CertNotAfter: gen.NotAfter,
        CertReused: !!gen.Reused, CertReusedFrom: gen.FromName || '',
      }));
      fetchConnections();
      showNotification({
        message: gen.Reused
          ? (t('certReusedMsg') || 'Certifikát prevzatý z existujúceho pripojenia — v Azure netreba nič nahrávať.')
          : (t('certGenerated') || 'Certifikát vygenerovaný — stiahnite .cer a nahrajte do Azure.'),
        type: NotificationType.Success,
      });
    } catch (e) {
      showNotification({ message: e?.response?.data?.detail || t('errorOccured'), type: NotificationType.Error });
    } finally { setGenLoading(false); }
  };

  const handleDownloadCert = async () => {
    if (editId === null) return;
    try {
      const blob = await downloadCertificate(editId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `SharePoint_${(form.Name || 'connection').replace(/[^\w.-]+/g, '_')}.cer`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      showNotification({ message: e?.response?.data?.detail || t('errorOccured'), type: NotificationType.Error });
    }
  };

  const runTest = async (id) => {
    setTesting(s => ({ ...s, [id]: true }));
    setTestResult(s => ({ ...s, [id]: undefined }));
    try {
      const res = await testConnection(id);
      setTestResult(s => ({ ...s, [id]: res }));
    } catch (e) {
      setTestResult(s => ({
        ...s,
        [id]: { ok: false, transportError: true, detail: e?.response?.data?.detail || String(e) },
      }));
    } finally { setTesting(s => ({ ...s, [id]: false })); }
  };

  const runModalTest = async () => {
    if (editId === null) return;
    setModalTest('loading');
    try { setModalTest(await testConnection(editId)); }
    catch (e) {
      setModalTest({ ok: false, detail: e?.response?.data?.detail || e?.message || String(e) });
    }
  };

  const beginDelegatedLogin = async () => {
    if (editId === null) {
      showNotification({ message: t('saveBeforeLogin') || 'Najprv uložte pripojenie.', type: NotificationType.Error });
      return;
    }
    // Popup treba otvoriť synchrónne v geste kliknutia (inak ho prehliadač zablokuje).
    const w = 520, h = 680;
    const dualLeft = window.screenLeft ?? window.screenX ?? 0;
    const dualTop = window.screenTop ?? window.screenY ?? 0;
    const vw = window.innerWidth || document.documentElement.clientWidth || window.screen.width;
    const vh = window.innerHeight || document.documentElement.clientHeight || window.screen.height;
    const left = Math.max(0, Math.round(dualLeft + (vw - w) / 2));
    const top = Math.max(0, Math.round(dualTop + (vh - h) / 2));
    const features = `width=${w},height=${h},left=${left},top=${top},menubar=no,toolbar=no,location=no,status=no`;
    const popup = window.open('', 'sp-oauth', features);
    popupRef.current = popup;
    setDeviceFlow({ status: 'starting' });
    try {
      const res = await startDelegatedLogin(editId, delegatedRedirectUri);
      if (popup) popup.location.href = res.authorizeUrl;
      else popupRef.current = window.open(res.authorizeUrl, 'sp-oauth', features); // fallback ak bol popup blokovaný
      setDeviceFlow({ status: 'pending' });
    } catch (e) {
      if (popup && !popup.closed) { try { popup.close(); } catch (err) { /* noop */ } }
      popupRef.current = null;
      setDeviceFlow({ status: 'error', detail: e?.response?.data?.detail || String(e) });
    }
  };

  const copyRedirectUri = async () => {
    try {
      await navigator.clipboard.writeText(delegatedRedirectUri);
      setCopiedRedirect(true);
      window.setTimeout(() => setCopiedRedirect(false), 1500);
    } catch (e) {
      showNotification({ message: delegatedRedirectUri, type: NotificationType.Info });
    }
  };

  const handleDelegatedLogout = async () => {
    if (editId === null) return;
    try {
      await logoutDelegated(editId);
      setForm(current => ({ ...current, DelegatedAccount: '', DelegatedAuthenticatedAt: null }));
      setDeviceFlow(null);
      fetchConnections();
    } catch (e) {
      showNotification({ message: e?.response?.data?.detail || t('errorOccured'), type: NotificationType.Error });
    }
  };


  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await deleteConnection(toDelete.Id);
      showNotification({ message: t('savedChanges'), type: NotificationType.Success });
      fetchConnections();
    } catch (e) {
      showNotification({ message: t('errorOccured'), type: NotificationType.Error });
    } finally { setToDelete(null); }
  };

  // Karta appkou spravovaného certifikátu (pripravený → stiahnuť/regenerovať; inak vygenerovať/hint).
  // Zdieľaná certifikátovým aj delegovaným režimom (delegované = tiež confidential klient s certom).
  const renderManagedCert = () => (
    form.HasGeneratedCer ? (
      <div className="sp-cert-ready">
        <div className="sp-cert-ready-head"><span className="material-symbols-outlined">verified_user</span>{t('certReady') || 'Certifikát je pripravený'}</div>
        {form.CertReused && (
          <div className="sp-cert-reused">
            <span className="material-symbols-outlined">check_circle</span>
            <span>{(t('certReusedFrom') || 'Certifikát prevzatý z pripojenia {name} — v Azure netreba nič nahrávať.').replace('{name}', `„${form.CertReusedFrom || '—'}"`)}</span>
          </div>
        )}
        {form.CertThumbprint && <div className="sp-cert-meta">{t('certThumbprint') || 'Odtlačok'}: <code>{form.CertThumbprint}</code></div>}
        {form.CertNotAfter && <div className="sp-cert-meta">{t('certExpiry') || 'Platí do'}: {formatDate(form.CertNotAfter, i18n.language)}</div>}
        <div className="sp-cert-actions">
          <button type="button" className={form.CertReused ? 'download-button' : 'submit-button'} onClick={handleDownloadCert}><span className="material-symbols-outlined">download</span>&nbsp;{t('downloadCer') || 'Stiahnuť .cer'}</button>
          <button type="button" className="download-button" onClick={() => handleGenerateCert(true)} disabled={genLoading}><span className="material-symbols-outlined">refresh</span>&nbsp;{t('regenerateCert') || 'Regenerovať'}</button>
        </div>
        {!form.CertReused && (
          <div className="sp-cert-steps">
            <span className="material-symbols-outlined">info</span>
            <span>{t('certAzureSteps') || 'Stiahnite .cer → v Azure otvorte aplikáciu → Certificates & secrets → Upload certificate → nahrajte .cer. Potom povoľte oprávnenia + Grant admin consent.'}</span>
          </div>
        )}
      </div>
    ) : (
      <>
        {appSibling && (
          <div className="sp-cert-hint">
            <span className="material-symbols-outlined">info</span>
            <span>{(t('sameAzureAppHint') || 'Rovnaká Azure app ako {name} — certifikát sa prevezme, netreba nič nahrávať do Azure.').replace('{name}', `„${appSibling.Name || appSibling.SiteUrl}"`)}</span>
          </div>
        )}
        {editId === null ? (
          <span className="sp-hint">{t('certWillGenerate') || 'Certifikát sa vygeneruje po uložení pripojenia. Potom stiahnete .cer na nahranie do Azure.'}</span>
        ) : (
          <button type="button" className="submit-button" onClick={() => handleGenerateCert(false)} disabled={genLoading}>
            <span className="material-symbols-outlined">shield</span>&nbsp;{genLoading ? (t('generating') || 'Generujem…') : (t('generateCert') || 'Vygenerovať certifikát')}
          </button>
        )}
      </>
    )
  );

  const renderTestBadge = (id) => {
    if (testing[id]) return <span className="sp-badge sp-badge-muted">…</span>;
    const r = testResult[id];
    if (!r) return null;
    if (r.transportError) {
      return <span className="sp-badge sp-badge-err"><span className="material-symbols-outlined">error</span>{t('connectionError') || 'Chyba'}</span>;
    }
    return (
      <span className="sp-test-stages">
        <span className={`sp-badge ${r.authOk ? 'sp-badge-ok' : 'sp-badge-err'}`}>
          <span className="material-symbols-outlined">{r.authOk ? 'check' : 'error'}</span>
          {r.authOk ? (t('tokenVerified') || 'Token OK') : (t('authenticationFailed') || 'Prihlásenie zlyhalo')}
        </span>
        {r.authOk && (
          <span className={`sp-badge ${r.siteOk ? 'sp-badge-ok' : 'sp-badge-err'}`}>
            <span className="material-symbols-outlined">{r.siteOk ? 'check' : 'error'}</span>
            {r.siteOk
              ? `${t('siteVerified') || 'Site OK'}${r.siteTitle ? ' – ' + r.siteTitle : ''}`
              : (t('siteUnavailable') || 'Site nedostupná')}
          </span>
        )}
      </span>
    );
  };

  // Iné pripojenie s rovnakou Azure app (tenant + client ID) — certifikát sa z neho prevezme.
  const appSibling = connections.find(
    c => c.Id !== editId && form.Tenant && form.ClientId
      && c.Tenant === form.Tenant && c.ClientId === form.ClientId
  );

  return (
    <div className="sp-card sp-card-flat">
      <div className="sp-card-head">
        <div className="sp-conn-head-left">
          <h3>{t('connections') || 'Pripojenia'}</h3>
          {connections.length > 0 && <span className="sp-conn-count">{connections.length}</span>}
        </div>
        <button className="submit-button sp-btn-compact" onClick={openAdd}>
          <span className="material-symbols-outlined">add</span>&nbsp;{t('addConnection') || 'Pridať pripojenie'}
        </button>
      </div>

      {connections.length > 0 && (
        <div className="sp-conn-summary">
          {connections.length} {t('connectionsLc') || 'pripojení'} · {connections.reduce((s, c) => s + (c.FoldersCount || 0), 0)} {t('foldersCount') || 'priečinkov'}
        </div>
      )}

      {connections.length === 0 && (
        <div className="sp-invite">{t('noConnectionsInvite') || 'Zatiaľ žiadne pripojenia — pridaj prvé pripojenie k SharePointu.'}</div>
      )}

      <div className="sp-conn-list">
        {connections.map(c => {
          const r = testResult[c.Id];
          const go = () => navigate('/SharePoint/connection/' + c.Id);
          return (
            <div
              key={c.Id}
              className={`sp-conn-card${c.Enabled === false ? ' disabled' : ''}`}
              onClick={go}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go(); } }}
            >
              <div className="sp-conn-main">
                <span className={`material-symbols-outlined sp-conn-ico${c.Enabled !== false ? ' on' : ' off'}`}>
                  {c.Enabled !== false ? 'cloud' : 'cloud_off'}
                </span>
                <div className="sp-conn-info">
                  <div className="sp-conn-name">{c.Name || c.SiteUrl}</div>
                  <div className="sp-conn-url" title={c.SiteUrl}>{c.SiteUrl}</div>
                  <div className="sp-conn-badges">
                    <span className={`sp-status ${c.Enabled !== false ? 'on' : 'off'}`}>
                      {c.Enabled !== false ? (t('enabled') || 'Zapnuté') : (t('disabled') || 'Vypnuté')}
                    </span>
                    <span className="sp-badge sp-badge-soft">
                      {c.AuthType === 'delegated'
                        ? (t('onBehalfOfUser') || 'OAuth 2.0 – On behalf of user')
                        : (t('oauthClientCredentialsShort') || 'OAuth 2.0 Client Credentials')}
                    </span>
                    {c.FoldersCount > 0 && (
                      <span className="sp-folder-chip">
                        <span className="material-symbols-outlined">folder</span>
                        {c.FoldersCount} {t('foldersCount') || 'priečinkov'}
                      </span>
                    )}
                    {renderTestBadge(c.Id)}
                  </div>
                </div>
                <div className="sp-conn-actions" onClick={(e) => e.stopPropagation()}>
                  <button type="button" className="sp-icon-btn" onClick={() => runTest(c.Id)} disabled={testing[c.Id]}
                    title={t('testConnection') || 'Test pripojenia'} aria-label={t('testConnection') || 'Test pripojenia'}>
                    <span className="material-symbols-outlined">wifi_tethering</span>
                  </button>
                  <button type="button" className="sp-icon-btn" onClick={() => openEdit(c)}
                    title={t('edit')} aria-label={t('edit')}>
                    <span className="material-symbols-outlined">edit</span>
                  </button>
                  <button type="button" className="sp-icon-btn danger" onClick={() => setToDelete(c)}
                    title={t('delete')} aria-label={t('delete')}>
                    <span className="material-symbols-outlined">delete</span>
                  </button>
                </div>
                <span className="sp-conn-chevron material-symbols-outlined" aria-hidden="true">chevron_right</span>
              </div>

              {r && !r.ok && (
                <div className="sp-errbox" onClick={(e) => e.stopPropagation()}>
                  <span className="material-symbols-outlined">error</span>
                  <div>
                    <div className="sp-errbox-title">{t('connectionError') || 'Chyba pripojenia'}</div>
                    <div className="sp-errbox-detail">{r.detail}</div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Modal open={openModal} onClose={closeModal} aria-labelledby="sp-conn-modal-title">
        <div className="env-modal sp-conn-modal">
          <div className="new-env-title">
            <h1>{editId === null ? (t('addConnection') || 'Nové pripojenie') : (t('editConnection') || 'Upraviť pripojenie')}</h1>
          </div>
          <div className="sp-auth-cards-label">{t('connectionMethod') || 'Spôsob pripojenia'}</div>
          <div className="sp-auth-cards" role="tablist" aria-label={t('connectionMethod') || 'Spôsob pripojenia'}>
            <button
              type="button"
              role="tab"
              aria-selected={form.AuthType === 'certificate'}
              className={`sp-auth-card${form.AuthType === 'certificate' ? ' selected' : ''}${editId !== null ? ' locked' : ''}`}
              onClick={() => setForm(current => ({ ...current, AuthType: 'certificate' }))}
              disabled={editId !== null}
              title={editId !== null ? (t('authTypeLocked') || 'Typ pripojenia sa po uložení nedá zmeniť.') : undefined}
            >
              <span className="material-symbols-outlined">verified_user</span>
              <span>
                <strong>{t('oauthClientCredentials') || 'OAuth 2.0 client credentials grant'}</strong>
                <small>{t('certificateCardHint') || 'Aplikačný prístup s certifikátom PFX'}</small>
              </span>
              {form.AuthType === 'certificate' && <span className="material-symbols-outlined sp-auth-card-check">check_circle</span>}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={form.AuthType === 'delegated'}
              className={`sp-auth-card${form.AuthType === 'delegated' ? ' selected' : ''}${editId !== null ? ' locked' : ''}`}
              onClick={() => setForm(current => ({ ...current, AuthType: 'delegated' }))}
              disabled={editId !== null}
              title={editId !== null ? (t('authTypeLocked') || 'Typ pripojenia sa po uložení nedá zmeniť.') : undefined}
            >
              <span className="material-symbols-outlined">account_circle</span>
              <span>
                <strong>{t('onBehalfOfUser') || 'OAuth 2.0 – On behalf of user'}</strong>
                <small>{t('delegatedCardHint') || 'Prihlásenie používateľa cez Microsoft'}</small>
              </span>
              {form.AuthType === 'delegated' && <span className="material-symbols-outlined sp-auth-card-check">check_circle</span>}
            </button>
          </div>
          <button
            type="button"
            className="sp-help-link"
            onClick={() => (form.AuthType === 'certificate' ? setShowCertHelp(true) : setShowDelegatedHelp(true))}
          >
            <span className="material-symbols-outlined">info</span>
            {form.AuthType === 'certificate'
              ? (t('howToConfigureCert') || 'Ako nastaviť SharePoint pripojenie?')
              : (t('howToConfigureDelegated') || 'Ako nastaviť prihlásenie používateľa?')}
          </button>
          <form className="new-env-input-form" autoComplete="off">
            <div className="input-line"><label>{t('name') || 'Názov'}</label>
              <input type="text" name="Name" value={form.Name} onChange={change} autoComplete="off" /></div>
            <div className="input-line"><label>{t('sharePointSiteUrl') || 'SharePoint Site URL'}</label>
              <input type="text" name="SiteUrl" value={form.SiteUrl} onChange={change} placeholder="https://firma.sharepoint.com/sites/Dokumenty" />
              <span className="sp-hint">{t('siteUrlHint') || 'Zadajte iba adresu webu alebo subsite. Knižnicu a priečinok vyberiete cez Spravovať priečinky.'}</span></div>
            <div className="input-line"><label>Application (client) ID</label>
              <input type="text" name="ClientId" value={form.ClientId} onChange={change} autoComplete="off" /></div>
            <div className="input-line"><label>Directory (tenant) ID</label>
              <input type="text" name="Tenant" value={form.Tenant} onChange={change} /></div>
            {form.AuthType === 'certificate' ? (
              <>
                <div className="input-line ad-group-toggle-row">
                  <label>{t('autoGenerateCert') || 'Vygenerovať certifikát automaticky'}</label>
                  <label className="switch"><input type="checkbox" name="AutoCert" checked={!!form.AutoCert} onChange={change} /><span className="slider"></span></label>
                </div>

                {form.AutoCert ? (
                  <div className="input-line">
                    {renderManagedCert()}
                  </div>
                ) : (
                  <>
                    <div className="input-line"><label>{t('certificate') || 'Certifikát'}</label>
                      <input
                        id="sp-certificate-file"
                        className="sp-file-input"
                        type="file"
                        accept=".pfx,application/x-pkcs12"
                        onChange={handleCertSelect}
                      />
                      <div className="sp-file-picker">
                        <label className="download-button sp-file-button" htmlFor="sp-certificate-file">
                          <span className="material-symbols-outlined" aria-hidden="true">upload_file</span>
                          {t('chooseCertificate') || 'Vybrať certifikát'}
                        </label>
                        <span className={`sp-file-name${certFile || form.HasCertificate ? ' has-file' : ''}`} title={certFile?.name || ''}>
                          {certFile?.name
                            || (form.HasCertificate
                              ? (form.CertificateFileName || t('certificateAlreadyUploaded') || 'Certifikát je už nahraný.')
                              : (t('noFileSelected') || 'Nebol vybraný žiadny súbor.'))}
                        </span>
                      </div>
                    </div>
                    <div className="input-line"><label>{t('certPassword') || 'Heslo certifikátu'}</label>
                      <input type="password" name="CertPassword" value={form.CertPassword} onChange={change}
                        autoComplete="new-password" placeholder={form.HasCertPassword ? t('passwordSetPlaceholder') : ''} /></div>
                  </>
                )}
              </>
            ) : (
              <div className="input-line">
                <label>User Delegated</label>

                <div className="sp-deleg-cert">
                  <span className="sp-redirect-uri-label">{t('certificate') || 'Certifikát'}</span>
                  {renderManagedCert()}
                  <span className="sp-input-help">{t('delegatedCertNote') || 'Delegované prihlásenie používa certifikát tejto aplikácie — musí byť vygenerovaný a .cer nahraný v Azure.'}</span>
                </div>

                <div className="sp-redirect-uri">
                  <span className="sp-redirect-uri-label">{t('redirectUriLabel') || 'Redirect URI (pre Azure)'}</span>
                  <div className="sp-redirect-uri-row">
                    <code>{delegatedRedirectUri}</code>
                    <button type="button" className="sp-icon-btn" onClick={copyRedirectUri}
                      title={t('copy') || 'Kopírovať'} aria-label={t('copy') || 'Kopírovať'}>
                      <span className="material-symbols-outlined">{copiedRedirect ? 'check' : 'content_copy'}</span>
                    </button>
                  </div>
                  <span className="sp-input-help">{t('redirectUriHint') || 'Túto adresu zaregistrujte v Azure → App registration → Authentication → Redirect URI configuration → Add Redirect URI → vyberte platformu Web (nie Single-page application) → vložte adresu → Save.'}</span>
                </div>

                <div className="sp-signin-row">
                  {form.DelegatedAccount
                    ? <div className="sp-delegated-state">
                        <span className="sp-badge sp-badge-ok"><span className="material-symbols-outlined">check</span>{form.DelegatedAccount}</span>
                        <button type="button" className="download-button sp-btn-compact" onClick={handleDelegatedLogout}>
                          {t('signOut') || 'Odhlásiť'}
                        </button>
                      </div>
                    : <button type="button" className="download-button sp-btn-compact" onClick={beginDelegatedLogin}
                        disabled={editId === null || deviceFlow?.status === 'starting' || deviceFlow?.status === 'pending'}>
                        {deviceFlow?.status === 'starting' || deviceFlow?.status === 'pending'
                          ? <><span className="material-symbols-outlined spin">progress_activity</span>&nbsp;{t('signingInShort') || 'Prihlasovanie…'}</>
                          : <><span className="material-symbols-outlined">login</span>&nbsp;{t('signInWithMicrosoft') || 'Prihlásiť cez Microsoft'}</>}
                      </button>}
                  {editId === null && <span className="sp-hint">{t('saveBeforeLogin') || 'Najprv uložte pripojenie.'}</span>}
                </div>
                {form.DelegatedAccount && form.DelegatedAuthenticatedAt && (() => {
                  const days = Math.floor((Date.now() - new Date(form.DelegatedAuthenticatedAt).getTime()) / 86400000);
                  const stale = days > 75; // 90-dňové okno refresh tokenu, ~15 dní vopred varuj
                  return (
                    <div className={`sp-verified${stale ? ' stale' : ''}`}>
                      <span className="material-symbols-outlined">{stale ? 'warning' : 'schedule'}</span>
                      <span>{(t('lastVerified') || 'Naposledy overené')}: {formatDate(form.DelegatedAuthenticatedAt, i18n.language)}{stale && <> — {t('reloginSoon') || 'prihlásenie starne, čoskoro sa prihláste znova (platnosť ~90 dní)'}</>}</span>
                    </div>
                  );
                })()}
                {deviceFlow?.status === 'error' && (
                  <div className="sp-errbox sp-signin-err">
                    <span className="material-symbols-outlined">error</span>
                    <div className="sp-errbox-detail">{deviceFlow.detail}</div>
                  </div>
                )}
              </div>
            )}
            <div className="input-line ad-group-toggle-row"><label>{t('enabled') || 'Povolené'}</label>
              <label className="switch"><input type="checkbox" name="Enabled" checked={!!form.Enabled} onChange={change} /><span className="slider"></span></label></div>

            {editId !== null && (
              <div className="input-line sp-modal-test">
                <button type="button" className="download-button sp-test-btn" onClick={runModalTest}>
                  <span className="material-symbols-outlined">wifi_tethering</span>&nbsp;{t('testConnection') || 'Test pripojenia'}
                </button>
                {modalTest === 'loading' && <span className="sp-hint">&nbsp;…</span>}
                {modalTest && modalTest !== 'loading' && (
                  <div className="sp-modal-test-result">
                    <span className="sp-test-stages">
                      <span className={`sp-badge ${modalTest.authOk ? 'sp-badge-ok' : 'sp-badge-err'}`}>
                        <span className="material-symbols-outlined">{modalTest.authOk ? 'check' : 'error'}</span>
                        {modalTest.authOk ? (t('tokenVerified') || 'Token OK') : (t('authenticationFailed') || 'Prihlásenie zlyhalo')}
                      </span>
                      {modalTest.authOk && (
                        <span className={`sp-badge ${modalTest.siteOk ? 'sp-badge-ok' : 'sp-badge-err'}`}>
                          <span className="material-symbols-outlined">{modalTest.siteOk ? 'check' : 'error'}</span>
                          {modalTest.siteOk
                            ? `${t('siteVerified') || 'Site OK'}${modalTest.siteTitle ? ' – ' + modalTest.siteTitle : ''}`
                            : (t('siteUnavailable') || 'Site nedostupná')}
                        </span>
                      )}
                    </span>
                    {!modalTest.ok && (
                      <div className="sp-errbox"><span className="material-symbols-outlined">error</span><div className="sp-errbox-detail">{modalTest.detail}</div></div>
                    )}
                  </div>
                )}
              </div>
            )}
          </form>
          <div className="sp-modal-footer">
            <button type="button" className="return-button" disabled={saving} onClick={closeModal}>{t('cancel')}</button>
            <button type="button" className="submit-button" disabled={saving} onClick={handleSave}>{t('save')}</button>
          </div>
        </div>
      </Modal>

      <CertHelpModal open={showCertHelp} onClose={() => setShowCertHelp(false)} />
      <DelegatedHelpModal open={showDelegatedHelp} onClose={() => setShowDelegatedHelp(false)} />

      <ConfirmDeleteModal
        open={toDelete !== null}
        onClose={() => setToDelete(null)}
        onNo={() => setToDelete(null)}
        onYes={handleDelete}
        item={toDelete ? (toDelete.Name || toDelete.SiteUrl) : ''}
      />
    </div>
  );
};

export default ConnectionsList;
